import { useState, useEffect, useCallback } from 'react';
import { usePeersByTopic } from '../../hooks/api/useBrowsePeers';
import { useBrowseFilters } from '../../hooks/utils/useBrowseFilters';
import { useTagGroups } from '../../hooks/api/useTagGroups';
import { PeerAvatar } from '../../components/peer-avatar';
import { DropdownPill } from '../../components/dropdown-pill';
import { SearchInput } from '../../components/search-input';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { usePeersonaAccess } from '../../hooks/utils/usePeersonaAccess';

const LISTENER_TYPE_OPTIONS = [
  { value: 'peer', label: 'Peer' },
  { value: 'peer_listener', label: 'Listener' },
  { value: 'peers_with_peersonas', label: 'Peersona' },
];

const BrowsePeers = () => {
  const INITIAL_ITEMS = 24;
  const LOAD_MORE_ITEMS = 12;
  const { isPeerPeersonaEnabled } = usePeersonaAccess();

  // Use shared filter hook for topic/subtopics and search
  const { selectedTagGroupId, setSelectedTagGroupId, selectedTagIds, searchQuery, setSearchQuery } = useBrowseFilters(
    false,
    true,
  );

  const { data: topicTagGroups } = useTagGroups();
  const [itemsShown, setItemsShown] = useState(INITIAL_ITEMS);

  const [selectedListenerType, setSelectedListenerType] = useState<string | undefined>(undefined);

  const {
    data: peersData,
    isLoading: isLoadingPeers,
    error: peersError,
  } = usePeersByTopic(
    selectedTagGroupId,
    selectedTagIds.length > 0 ? selectedTagIds : undefined,
    selectedListenerType ? [selectedListenerType] : undefined,
    searchQuery && searchQuery.trim().length > 0 ? searchQuery : undefined,
  );

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery?.(query);
    },
    [setSearchQuery],
  );

  // Reset itemsShown when filters or search change
  useEffect(() => {
    setItemsShown(INITIAL_ITEMS);
  }, [selectedTagIds, selectedTagGroupId, selectedListenerType, searchQuery]);

  const loadMoreItems = useCallback(() => {
    if (peersData?.data && peersData.data.length > itemsShown) {
      setItemsShown((prev) => Math.min(prev + LOAD_MORE_ITEMS, peersData.data.length));
    }
  }, [peersData?.data, itemsShown]);

  const dfMdMedia = useMediaQuery('md');

  return (
    <>
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className={`flex-1 ${!dfMdMedia ? 'pt-[76px]' : ''}`}>
          <div className="max-w-page mx-auto px-6 py-8">
            <div className="mb-6">
              <h1 className="font-open-sans mb-6 text-3xl font-semibold leading-[60px] text-black">Peers</h1>
              <div className={`mb-6 flex items-start justify-start gap-3 ${dfMdMedia ? 'flex-row' : 'flex-col'}`}>
                <SearchInput onSearch={handleSearch} initialValue={searchQuery || ''} className="mb-0" />
                {/* Listener Type Filter */}
                <DropdownPill
                  label="All Peers"
                  placeholder="All Peers"
                  options={LISTENER_TYPE_OPTIONS}
                  selectedValue={selectedListenerType}
                  onSelect={(value) => setSelectedListenerType(value as string | undefined)}
                />
                <DropdownPill
                  label="Filter by topic"
                  placeholder="Filter by topic"
                  options={[
                    { value: undefined, label: 'All Topics' },
                    ...(topicTagGroups || []).map((tg) => ({ value: tg.id, label: tg.name })),
                  ]}
                  selectedValue={selectedTagGroupId}
                  onSelect={(value) => setSelectedTagGroupId(value as number | undefined)}
                />
              </div>
            </div>

            {/* Peers Display */}
            {peersError ? (
              <div className="text-center text-red-500">Error loading peers. Please try again.</div>
            ) : (
              <div>
                <div className="flex flex-wrap items-start justify-start gap-x-6 gap-y-12">
                  {isLoadingPeers
                    ? Array(8)
                        .fill(undefined)
                        .map((_, index) => <PeerAvatar key={`skeleton-${index}`} isLoading={true} />)
                    : peersData?.data
                        .slice(0, itemsShown)
                        .map((peer) => (
                          <PeerAvatar
                            key={peer.listener_role_id}
                            peer={peer}
                            isPeersona={isPeerPeersonaEnabled(peer.listener_role_id, peer.chatbot_id)}
                          />
                        ))}
                </div>
                {!isLoadingPeers && peersData && (
                  <>
                    {peersData.data.length > itemsShown && (
                      <div className="mt-6 flex justify-center">
                        <button
                          onClick={loadMoreItems}
                          className="bg-button-secondary inline-flex h-10 items-center justify-center gap-1.5 rounded-full px-6 py-2.5"
                        >
                          <div className="text-primary font-open-sans text-base font-medium leading-5">See more</div>
                        </button>
                      </div>
                    )}
                    {peersData.count === 0 && (
                      <div className="text-center text-gray-500">
                        <p className="text-lg">No peers found.</p>
                        {(selectedTagGroupId || selectedTagIds.length > 0 || selectedListenerType || searchQuery) && (
                          <p className="mt-2 text-sm">Try adjusting your filters or search.</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default BrowsePeers;
